import {useMemo} from 'react'
import {useQuery} from '@tanstack/react-query'
import {Plus} from 'lucide-react'
import {useNavigate} from 'react-router-dom'
import {usePageTitle} from '@/hooks/use-page-title'
import {useServerPagination, SortDir} from '@/hooks/use-server-pagination'
import {useEmbeddedListProps} from '@/hooks/use-embedded-list-props'
import {CertificationPolicyControllerPoliciesEndpoint} from '@/types/endpoint-contracts'
import {Button} from '@/components/ui/button'
import {TableEmptyState} from '@/components/shared/TableEmptyState'
import {PageLayout} from '@/components/shared/PageLayout'
import {Badge, BadgeVariant} from '@/components/ui/badge'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import {CONCEPT_ICON} from '@/lib/concepts'
import {SortableHeader} from '@/components/ui/sortable-header'
import {Pagination} from '@/components/ui/pagination'
import {useI18n} from '@/i18n'
import {useServices} from '@/services/context'
import {routes} from '@/lib/routes'
import {permissions} from '@/lib/permissions'
import {UnifiedSearchBar} from '@/components/shared/UnifiedSearchBar'
import {ClickableTableRow} from '@/components/shared/ClickableTableRow'
import {TextLink} from '@/components/shared/TextLink'
import {MutedText} from '@/components/shared/MutedText'
import {InlineRow} from '@/components/shared/InlineRow'
import {Icon} from '@/components/shared/Icon'

/**
 * Certification Policies list page — Phase C.3 unified search migration (issue 342.08).
 * Replaces the legacy SelectItem status filter + SearchInput with a single
 * {@link UnifiedSearchBar} driven by the
 * {@link CertificationPolicyControllerPoliciesEndpoint} contract.
 *
 * Spec invariants this component upholds:
 *   - Zero implicit default filters — an empty URL shows every policy.
 *   - One request per submit / chip removal.
 *   - `q` matches the policy's @Searchable name field via the
 *     SearchableContractRegistry path.
 */
interface CertificationPoliciesProps {
    embedded?: boolean
    ownedByMe?: boolean
    emptyStateTitle?: string
}

export function CertificationPolicies({embedded, ownedByMe, emptyStateTitle}: CertificationPoliciesProps = {}) {
    const i18n = useI18n()
    const services = useServices()
    usePageTitle(i18n.certificationPolicies.pageTitle)
    const navigate = useNavigate()

    const {
        page, pageSize, sortBy, sortDir, queryString,
        setSort, setPage, setPageSize, pageSizeOptions,
        searchAndFilters, setSearchAndFilters,
    } = useServerPagination({
        endpoint: CertificationPolicyControllerPoliciesEndpoint,
        defaultSortBy: CertificationPolicyControllerPoliciesEndpoint.sort.PRIORITY,
        defaultSortDir: SortDir.ASC,
        sortable: true,
    })

    const {listParams, canCreate, title, emptyMessage} = useEmbeddedListProps({
        contract: CertificationPolicyControllerPoliciesEndpoint,
        ownedByMe,
        baseQueryString: queryString,
        emptyStateTitle,
        defaultTitle: i18n.certificationPolicies.allPolicies,
        createPermission: permissions.certificationPolicies.create,
    })

    const {data: policiesData, isLoading} = useQuery({
        queryKey: ['certification-policies', queryString, page, pageSize, sortBy, sortDir, ownedByMe],
        queryFn: () => services.certification.listPolicies(listParams),
    })

    const policies = policiesData?.content || []
    const totalElements = policiesData?.totalElements || 0
    const totalPages = policiesData?.totalPages || 0

    const hasActiveFilters = useMemo(() => {
        return !!searchAndFilters.q
            || Object.keys(searchAndFilters.filters).length > 0
            || Object.keys(searchAndFilters.dynamicAttributes).length > 0
    }, [searchAndFilters])

    return (
        <PageLayout
            variant="list"
            icon={CONCEPT_ICON.certificationCampaign}
            title={title}
            count={totalElements}
            description={`${i18n.certificationPolicies.policiesCount({count: totalElements})}${hasActiveFilters ? ` ${i18n.common.matchingFilters}` : ''}`}
            data-testid="certification-policies-list"
            embedded={embedded}
            actions={canCreate ? (
                <Button onClick={() => navigate(routes.accessGovernance.certificationPolicies.create())} data-testid="btn-create-policy">
                    <Icon icon={Plus} size="md" className="mr-2"/>
                    {i18n.certificationPolicies.createPolicy}
                </Button>
            ) : undefined}
            filters={
                <UnifiedSearchBar
                    contract={CertificationPolicyControllerPoliciesEndpoint}
                    value={searchAndFilters}
                    onChange={setSearchAndFilters}
                    placeholder={i18n.certificationPolicies.searchPolicies}
                />
            }
            pagination={totalPages > 0 ? (
                <Pagination
                    page={page}
                    pageSize={pageSize}
                    totalPages={totalPages}
                    totalItems={totalElements}
                    onPageChange={setPage}
                    onPageSizeChange={setPageSize}
                    pageSizeOptions={pageSizeOptions}
                />
            ) : undefined}
        >
            <Table>
                <TableHeader>
                    <TableRow>
                        <TableHead data-testid="col-name">
                            <SortableHeader label={i18n.certificationPolicies.name} sortKey={CertificationPolicyControllerPoliciesEndpoint.sort.NAME} currentSortBy={sortBy} currentSortDir={sortDir} onSort={setSort}/>
                        </TableHead>
                        <TableHead data-testid="col-priority">
                            <SortableHeader label={i18n.certificationPolicies.priority} sortKey={CertificationPolicyControllerPoliciesEndpoint.sort.PRIORITY} currentSortBy={sortBy} currentSortDir={sortDir} onSort={setSort}/>
                        </TableHead>
                        <TableHead data-testid="col-conditions">{i18n.certificationPolicies.conditions}</TableHead>
                        <TableHead data-testid="col-frequency">{i18n.certificationPolicies.frequency}</TableHead>
                        <TableHead data-testid="col-status">
                            <SortableHeader label={i18n.common.status} sortKey={CertificationPolicyControllerPoliciesEndpoint.sort.ACTIVE} currentSortBy={sortBy} currentSortDir={sortDir} onSort={setSort}/>
                        </TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {isLoading && (
                        <TableEmptyState
                            colSpan={5}
                            hasFilters={false}
                            matchMessage={i18n.certificationPolicies.loadingPolicies}
                            emptyMessage={i18n.certificationPolicies.loadingPolicies}
                        />
                    )}
                    {!isLoading && policies.map((policy) => (
                        <ClickableTableRow
                            key={policy.id}
                            to={routes.accessGovernance.certificationPolicies.detail(policy.id)}
                            data-testid={`policy-row-${policy.id}`}
                        >
                            <TableCell className="font-medium">
                                <InlineRow gap="sm">
                                    <TextLink
                                        to={routes.accessGovernance.certificationPolicies.detail(policy.id)}
                                        testId={`link-policy-${policy.id}`}
                                        stopPropagation
                                    >
                                        <span data-testid={`policy-name-${policy.id}`}>{policy.name}</span>
                                    </TextLink>
                                    {policy.isDefault && (
                                        <Badge variant="secondary" data-testid={`policy-default-${policy.id}`}>{i18n.certificationPolicies.defaultBadge}</Badge>
                                    )}
                                </InlineRow>
                                {policy.description && (
                                    <MutedText size="sm" className="block">
                                        {policy.description}
                                    </MutedText>
                                )}
                            </TableCell>
                            <TableCell>
                                <Badge variant="outline" data-testid={`policy-priority-${policy.id}`}>{policy.priority}</Badge>
                            </TableCell>
                            <TableCell>
                                {policy.conditionExpr != null ? (
                                    <Badge variant="secondary" data-testid={`policy-scope-expression-${policy.id}`}>
                                        {i18n.certificationPolicies.scopeModeExpressionBadge}
                                    </Badge>
                                ) : (
                                    <Badge variant="outline" data-testid={`policy-conditions-${policy.id}`}>
                                        {i18n.certificationPolicies.conditionsCount({count: policy.conditions.length})}
                                    </Badge>
                                )}
                            </TableCell>
                            <TableCell data-testid={`policy-frequency-${policy.id}`}>
                                {policy.parameter?.reviewFrequencyDays
                                    ? i18n.certificationPolicies.frequencyDays({days: policy.parameter.reviewFrequencyDays})
                                    : i18n.certificationPolicies.noFrequency}
                            </TableCell>
                            <TableCell>
                                <Badge variant={policy.active ? BadgeVariant.SUCCESS : BadgeVariant.SECONDARY} data-testid={`policy-status-${policy.id}`}>
                                    {policy.active ? i18n.common.active : i18n.common.inactive}
                                </Badge>
                            </TableCell>
                        </ClickableTableRow>
                    ))}
                    {!isLoading && policies.length === 0 && (
                        <TableEmptyState
                            colSpan={5}
                            hasFilters={hasActiveFilters}
                            matchMessage={i18n.certificationPolicies.noPoliciesMatch}
                            emptyMessage={emptyMessage ?? i18n.certificationPolicies.noPoliciesFound}
                        />
                    )}
                </TableBody>

            </Table>
        </PageLayout>
    )
}
